import { isUserRole } from '@/utils/typeGuards';
import {
  ActionFunctionArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  Params,
  redirect,
} from 'react-router-dom';
import { FirestoreError } from 'firebase/firestore';
import { UserRole } from '@/types/types';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { assert } from '@/utils/asserts';
import { OrganizationsService } from '@/services/organizationsService';
import { WorkspacesService } from '@/services/workspacesService';
import { OrganizationSettingsView } from './OrganizationSettingsView';
import { useWorkspaceIdStore } from '@/stores/workspaceIdStore';
import { showToast } from '@/utils/toasts';
import { InvitationsSchema } from '@/components/layouts/Modals/InviteUsersModal/InviteUsersModal';
import { formDataToObject } from '@/utils/helpers';
import { sanitizeCallableErrorMessage } from '@/utils/sanitizeCallableErrorMessage';
import { hasPermissionMessage } from '@/auth/hasPermission';
import { handleInvitationError, parseConstraints } from './helpers';

export function OrganizationSettings() {
  return <OrganizationSettingsView />;
}

const permissionsLoader: LoaderFunction = async () => {
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
  const organizationId = selectedOrganization?.id;
  if (!organizationId)
    throw { code: 'no-organization-id', message: 'No organization id provided' };
  if (!selectedOrganization.isOrganizationMember) return null;
  return OrganizationsService.getPermissions(organizationId);
};

const invitationsLoader: LoaderFunction = async () => {
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
  const organizationId = selectedOrganization?.id;
  if (!organizationId)
    throw { code: 'no-organization-id', message: 'No organization id provided' };
  if (!selectedOrganization.isOrganizationMember) return null;
  return await OrganizationsService.getInvitations(organizationId);
};

const subscriptionDetailsLoader: LoaderFunction = async () => {
  const organizationPermissions =
    useUserPermissionsStore.getState().organizationPermission?.role;
  const organizationId = useOrganizationStore.getState().selectedOrganization?.id;
  if (!organizationId)
    throw { code: 'no-organization-id', message: 'No organization id provided' };
  if (organizationPermissions !== 'admin') return null;
  return await OrganizationsService.getSubscriptionDetails(organizationId);
};

export const organizationSettingsLoader = async (args: LoaderFunctionArgs) => {
  const [permissions, invitations, subscriptionDetails] = await Promise.all([
    permissionsLoader(args),
    invitationsLoader(args),
    subscriptionDetailsLoader(args),
  ]);
  return { permissions, invitations, subscriptionDetails };
};

type PUTRequestType = 'updateEntityName' | 'changeUserRole';
type DELETERequestType =
  | 'invitation'
  | 'permission'
  | 'workspace'
  | 'selectedUsers'
  | 'organization';

export async function organizationSettingsActions({
  params,
  request,
}: ActionFunctionArgs) {
  const data = await request.formData();
  if (request.method === 'DELETE') {
    const reqType = data.get('type') as DELETERequestType;
    return await handleDeletion(reqType, params, data);
  } else if (request.method === 'POST') {
    return await createOrganizationInvitations(params, data);
  } else if (request.method === 'PUT') {
    const reqType = data.get('type') as PUTRequestType;
    return await handleUpdating(data, reqType);
  }

  return null;
}

async function handleUpdating(data: FormData, reqType: PUTRequestType) {
  const organizationId = useOrganizationStore.getState().selectedOrganization
    ?.id as string;
  if (reqType === 'updateEntityName') {
    return await updateOrganizationName(data, organizationId);
  } else {
    return await changeUserRole(data, organizationId);
  }
}

async function updateOrganizationName(data: FormData, organizationId: string) {
  try {
    const name = data.get('name') as string;
    return await OrganizationsService.updateName(organizationId, name);
  } catch (e) {
    console.error(e);
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to update organization name. Try again later!', 'error');
    console.error(error);
    return { message: sanitizeCallableErrorMessage(e) };
  }
}

async function changeUserRole(data: FormData, organizationId: string) {
  try {
    const permissionId = data.get('permissionId') as string;
    const role = data.get('role') as string;
    if (!isUserRole(role)) return null;
    await OrganizationsService.changeRole(organizationId, permissionId, role);
    return null;
  } catch (e) {
    console.error(e);
    showToast('Error!', 'Failed to change user role. Try again later!', 'error');
    return { changeRoleError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

type CreateInvitationResult = { success: true } | { success: false; message: string };

async function createOrganizationInvitations(
  _params: Params,
  formData: FormData,
): Promise<CreateInvitationResult> {
  const organizationId = useOrganizationStore.getState().selectedOrganization?.id;
  const data = formDataToObject<
    Omit<InvitationsSchema, 'emails'> & {
      emails: string[];
      invitationsNum: string;
      usersNum: string;
    }
  >(formData);
  if (!organizationId) {
    return { success: false, message: 'No organization selected' };
  }

  try {
    const emails = data.emails;
    const role = data.role;
    const { currentInvitationsNum, currentUsersNum } = parseConstraints(
      data.invitationsNum,
      data.usersNum,
    );

    const [, message] = hasPermissionMessage('organization', 'invitations', 'create', {
      currentInvitationsNum,
      currentUsersNum,
      newInvitationsNum: emails.length,
    });

    if (message) {
      throw new Error(message);
    }

    await OrganizationsService.createInvitations(organizationId, emails, role);
    return { success: true };
  } catch (err) {
    return handleInvitationError(err);
  }
}

async function handleDeletion(
  reqType: DELETERequestType,
  params: Params,
  data: FormData,
) {
  if (reqType === 'invitation') {
    return await deleteInvitation(reqType, params, data);
  } else if (reqType === 'permission') {
    return await deletePermission(params, data);
  } else if (reqType === 'selectedUsers') {
    return await deleteSelectedUsers(params, data);
  } else if (reqType === 'organization') {
    return await deleteOrganization();
  } else if (reqType === 'workspace') {
    return await deleteWorkspace(params, data);
  }
}

async function deleteSelectedUsers(params: Params, data: FormData) {
  const organizationId = useOrganizationStore.getState().selectedOrganization
    ?.id as string;
  const selected = JSON.parse(data.get('selected') as string) as {
    id: string;
    role: UserRole | string;
    hash?: string;
  }[];
  try {
    return await OrganizationsService.deleteSelectedUsers(organizationId, selected);
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete selected users. Try again later!', 'error');
    console.error(error);
    return { deleteSelectedError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deleteInvitation(reqType: string, params: Params, data: FormData) {
  const invitationId = data.get('invitationId') as string;
  const organizationId = useOrganizationStore.getState().selectedOrganization
    ?.id as string;
  try {
    return await OrganizationsService.deleteInvitation(invitationId, organizationId);
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete invitation. Try again later!', 'error');
    console.error(error);
    return { deleteInvitationError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deletePermission(params: Params, data: FormData) {
  const organizationId = useOrganizationStore.getState().selectedOrganization
    ?.id as string;
  const permissionId = data.get('permissionId') as string;
  try {
    await OrganizationsService.deletePermission(permissionId, organizationId);
    return redirect('/');
  } catch (e) {
    console.error(e);
    showToast('Error!', 'Failed to delete user. Try again later!', 'error');
    return { deletePermissionError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deleteOrganization() {
  const organizationId = useOrganizationStore.getState().selectedOrganization
    ?.id as string;
  const region = useOrganizationStore.getState().selectedOrganization?.region as string;
  try {
    await OrganizationsService.deleteOrganization(organizationId, region);
    useOrganizationStore.getState().reset();
    useUserPermissionsStore.getState().reset();
    useWorkspaceIdStore.getState().resetWorkspaceId();
    return redirect('/');
  } catch (e) {
    console.error(e);
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete organization. Try again later!', 'error');
    console.error(error);
    return { deleteWorkspaceError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deleteWorkspace(params: Params, data: FormData) {
  const workspaceId = data.get('workspaceId') as string;
  const organization = useOrganizationStore.getState().selectedOrganization;
  assert(organization, 'organization is undefined');
  try {
    await WorkspacesService.deleteWorkspace(
      workspaceId,
      organization.id,
      organization.region,
    );
    return null;
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete workspace. Try again later!', 'error');
    console.error(error);
    return { deleteWorkspaceError: { message: sanitizeCallableErrorMessage(e) } };
  }
}
